import React, { useEffect, useState } from "react";
import eclipseRight from "@/assets/images/eclipseRight.png";
import eclipseLeft from "@/assets/images/eclipseLeft.png";
import PricingToggler from "@/features/onboarding/components/Pricing/PricingToggler";
import starterIcon from "@/assets/svg/starter.svg";
import growthIcon from "@/assets/svg/growth.svg";
import enterpriseIcon from "@/assets/svg/enterprise.svg";
import AnimatedButton from "@/shared/components/ui/Button/AnimatedButton";
import { ArrowRight, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useMainStore } from "@/stores";
import { clearForcePostOnboardingPricing } from "@/shared/utils/helperFunctions";

const Pricing = () => {
  const [isMonthly, setIsMonthly] = useState(true);
  const navigate = useNavigate();
  const { setUser } = useMainStore();

  useEffect(() => {
    clearForcePostOnboardingPricing();
  }, []);

  const handlePlanAction = (link) => {
    setUser({ pricingPageCompleted: true });
    navigate(link);
  };
  const PlansDetails = [
    {
      id: 1,
      name: "Starter",
      priceMonthly: "$150",
      priceYearly: "$100",
      perMonthly: "mo",
      whatsIncluded: [
        "3 Users",
        "500 AI Credits / month",
        "1000 Contacts included*",
        "50 Accounts included*",
      ],
      icon: starterIcon,
      buttonText: "Try it FREE",
      buttonLink: "/join-organization",
      features: [
        {
          id: 1,
          name: "Full AI Outreach Suite",
          subfeatures: [
            "Email personalization",
            "Lead scoring",
            "Contact & company profiling",
            "Buying signal detection",
          ],
        },
        {
          id: 2,
          name: "Multi-channel Engagement (Email, LinkedIn, WhatsApp)",
        },
        {
          id: 3,
          name: "Deep Account Research & Account Planning",
        },
        {
          id: 4,
          name: "Conversation Summary & Sentiment Analysis",
        },
        {
          id: 5,
          name: "Basic Dashboards & Reporting",
        },
      ],
    },
    {
      id: 2,
      name: "Growth",
      priceMonthly: "$250",
      priceYearly: "$150",
      perMonthly: "mo",
      whatsIncluded: [
        "6 Users",
        "1000 AI Credits / month",
        "1500 Contacts included*",
        "100 Accounts included*",
      ],
      features: [
        {
          id: 1,
          name: "All Starter features, plus:",
        },
        {
          id: 2,
          name: "CRM Integrations (Zoho, HubSpot, Salesforce)",
        },
        {
          id: 3,
          name: "Advanced Multi-channel Sequencing",
        },
        {
          id: 4,
          name: "Advanced Dashboards & Reporting",
        },
        {
          id: 5,
          name: "Onboarding & Training Included",
        },
        {
          id: 6,
          name: "Dedicated Account Manager",
        },
        {
          id: 7,
          name: "Initial ABM Strategy & Account Prioritization Setup",
        },
      ],
      icon: growthIcon,
      buttonText: "Try it FREE",
      buttonLink: "/join-organization",
    },
    {
      id: 3,
      name: "Enterprise",
      priceMonthly: "$2500+",
      priceYearly: "$2000+",
      perMonthly: "mo",
      buttonText: "Book a Demo",
      buttonLink: "/demo-request",
      whatsIncluded: [
        "No. of Users: Custom",
        "AI Credits / month: Custom",
        "No. of Contacts included: Custom",
        "No. of Accounts included: Custom",
      ],
      icon: enterpriseIcon,
      features: [
        {
          id: 1,
          name: "All Growth features, plus:",
        },
        {
          id: 2,
          name: "AI Voice Personalization",
        },
        {
          id: 3,
          name: "Workflow Automation & Alerts",
        },
        {
          id: 4,
          name: "Enterprise-grade Dashboards & Reporting",
        },
        {
          id: 5,
          name: "White-space & Opportunity Mapping",
        },
        {
          id: 6,
          name: "Buying Group & Stakeholder Mapping",
        },
        {
          id: 7,
          name: "Enterprise Security Controls",
        },
      ],
    },
  ];
  return (
    <div className="flex flex-col p-[58px] relative">
      <img
        src={eclipseRight}
        alt="eclipse right"
        className="absolute top-0 right-0 w-[540px] h-[450px]"
      />
      <img
        src={eclipseLeft}
        alt="eclipse left"
        className="absolute top-0 left-0 w-[540px] h-[390px]"
      />

      <h2 className="text-[2.05rem] leading-11 font-inter font-semibold text-center text-gray-800">
        Maximize Your Outreach, Minimize Your Effort
      </h2>
      <h2 className="text-[2.05rem] leading-11 font-inter font-semibold text-center text-secondary">
        Start with AI. Scale with Strategy.
      </h2>

      <div className="flex flex-col items-center gap-5 pt-11">
        {/* Toggle Switcher */}
        <PricingToggler isMonthly={isMonthly} setIsMonthly={setIsMonthly} />
        {/* Content Area */}
        <div className="flex flex-col gap-5">
          <div className="flex gap-5 h-full">
            {PlansDetails.map((plan) => (
              <div key={plan.id}>
                <div className="flex flex-col pb-6 gap-4 rounded-[20px] border border-[#28A6CCB2] min-w-[350px] h-full">
                  <div className="py-6 flex flex-col gap-3.5 rounded-t-[20px]  bg-[linear-gradient(103deg,rgba(32,124,151,0.30)_0%,rgba(49,196,141,0.00)_100%)]">
                    <img
                      src={plan.icon}
                      alt="starter"
                      className="w-7 h-7 mx-auto"
                    />
                    <h4 className="text-secondary text-sm font-medium text-center ">
                      {plan.name}
                    </h4>
                    <p className="text-gray-800 font-bold flex items-center justify-center gap-2 text-[1.6rem] text-center ">
                      {isMonthly ? plan.priceMonthly : plan.priceYearly}
                      <span className="text-gray-800 text-[0.9rem] text-center font-normal ">
                        /{plan.perMonthly}
                      </span>
                    </p>
                  </div>
                  <div className="px-4">
                    <AnimatedButton
                      title={plan.buttonText}
                      onClick={() => handlePlanAction(plan.buttonLink)}
                    />
                  </div>
                  {/* what's included */}
                  <div className="flex flex-col">
                    {plan.whatsIncluded.map((item) => (
                      <div className="py-2 px-4" key={item}>
                        <p className="flex items-center gap-2 text-gray-700 text-sm font-medium">
                          <Check size={16} color="#28A6CC" /> {item}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="h-[1px] bg-[#28A6CC] mx-4 mb-2 mt-1 z-10 relative" />

                  {plan?.features?.map((feature) => (
                    <div key={feature?.id} className="px-4">
                      <p className="text-gray-600 flex items-center gap-1.5 text-sm">
                        <ArrowRight size={16} className="text-gray-400" />
                        {feature.name}
                      </p>
                      {feature.subfeatures && (
                        <ul className="list-disc list-inside text-gray-600 text-sm  ml-8">
                          {feature.subfeatures.map((subfeature) => (
                            <li key={subfeature}>{subfeature}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}

                  <button className="text-secondary text-left flex h-full items-end px-4 text-[0.8rem] font-medium underline">
                    View all features
                  </button>
                </div>
              </div>
            ))}
          </div>
          {/* additional info */}
          <p className="text-gray-800 text-[0.85rem] ">
            <sup>*</sup>Additional contacts:{" "}
            <span className="font-semibold"> $1 per contact</span> | Additional
            accounts: <span className="font-semibold"> $5 per account </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
